/**
 * Attendance module navigation — single source of truth (MVP).
 */

import {
  ATTENDANCE_CALENDAR_MASTER_PATH,
  attendanceCalendarTabUrl,
  attendanceImportFocusUrl,
} from "../../components/pages/Attendance/attendanceCalendarMasterConfig";

/** Primary sidebar + in-module tab navigation */
export const ATTENDANCE_MODULE_NAV = [
  {
    id: "dashboard",
    path: "/attendance/dashboard",
    label: "Attendance Dashboard",
    roles: ["admin", "hr", "manager", "employee"],
  },
  {
    id: "mark",
    path: "/attendance/mark",
    label: "Mark Attendance",
    roles: ["admin", "hr"],
  },
  {
    id: "records",
    path: "/attendance/records",
    label: "Attendance Records",
    roles: ["admin", "hr"],
  },
  {
    id: "calendar",
    path: "/attendance/apply-calendar",
    label: "Attendance Calendar",
    roles: ["admin", "hr"],
  },
  {
    id: "ot-hours",
    path: "/attendance/ot-hours",
    label: "Overtime",
    roles: ["admin", "hr"],
    requiresOtHoursAllocation: true,
  },
  {
    id: "holidays",
    path: "/holiday-calendar",
    label: "Holiday Calendar",
    roles: ["admin", "hr", "manager", "employee"],
  },
  {
    id: "devices",
    path: "/attendance/devices",
    label: "Biometric Devices",
    roles: ["admin", "hr"],
  },
  {
    id: "device-mapping",
    path: "/attendance/employee-device-mapping",
    label: "Device Mapping",
    roles: ["admin", "hr"],
  },
];

/** Legacy paths still reachable from quick actions */
export const ATTENDANCE_LEGACY_PATHS = {
  import: attendanceImportFocusUrl(),
  daily: attendanceCalendarTabUrl("daily"),
  master: ATTENDANCE_CALENDAR_MASTER_PATH,
};

/**
 * Page header tabs — empty; all attendance nav lives in the sidebar.
 */
export const ATTENDANCE_PAGE_TAB_IDS = new Set([]);

/** Tabs shown under attendance layout header (none — use sidebar). */
export const ATTENDANCE_QUICK_TABS = [];

export const ATTENDANCE_SECTIONS = [
  {
    id: "attendance",
    title: "Attendance",
    description: "Dashboard, marking, records, calendar and holidays.",
    items: [
      { path: "/attendance/dashboard", label: "Attendance dashboard", desc: "Overview, KPIs and alerts", roles: ["admin", "hr"] },
      { path: "/attendance/mark", label: "Mark attendance", desc: "Record one employee for one work date", roles: ["admin", "hr"] },
      { path: "/attendance/records", label: "Attendance records", desc: "Filter and manage attendance entries", roles: ["admin", "hr"] },
      { path: "/attendance/apply-calendar", label: "Attendance calendar", desc: "Month-wise visual attendance and lock period", roles: ["admin", "hr"] },
      {
        path: "/attendance/ot-hours",
        label: "OT hours allocation",
        desc: "Allocate overtime by day for payroll OVERTIME",
        roles: ["admin", "hr"],
        requiresOtHoursAllocation: true,
      },
      { path: "/holiday-calendar", label: "Holiday calendar", desc: "Organisation holidays", roles: ["admin", "hr"] },
    ],
  },
  {
    id: "biometric",
    title: "Biometric",
    description: "Devices, enrollment mapping, and sync.",
    items: [
      { path: "/attendance/devices", label: "Biometric devices", desc: "Registry, health, sync, providers", roles: ["admin", "hr"] },
      { path: "/attendance/employee-device-mapping", label: "Employee device mapping", desc: "Device user ID and enrollment metadata", roles: ["admin", "hr"] },
    ],
  },
];

export function filterSectionsByRole(sections, role, options = {}) {
  const otOn = options.otHoursAllocationEnabled !== false;
  const r = String(role || "").toLowerCase();
  const match = (roles) =>
    !roles || roles.some((x) => String(x).toLowerCase() === r);
  return sections
    .filter((sec) => match(sec.roles))
    .map((sec) => ({
      ...sec,
      items: sec.items.filter((it) => {
        if (it.requiresOtHoursAllocation && !otOn) return false;
        return match(it.roles);
      }),
    }))
    .filter((sec) => sec.items.length > 0);
}

export function filterAttendanceNavByRole(role, options = {}) {
  const otOn = options.otHoursAllocationEnabled !== false;
  const r = String(role || "").toLowerCase();
  return ATTENDANCE_MODULE_NAV.filter((item) => {
    if (item.requiresOtHoursAllocation && !otOn) return false;
    return item.roles.some((x) => String(x).toLowerCase() === r);
  });
}

export function filterAttendancePageTabsByRole(role, options = {}) {
  return filterAttendanceNavByRole(role, options);
}

export function isAttendanceNavActive(pathname, search, item) {
  if (item.id === "dashboard") {
    return pathname === "/attendance/dashboard" || pathname === "/attendance" || pathname === "/attendance/";
  }
  if (item.id === "import") {
    return pathname.startsWith("/attendance/apply-calendar") && search.includes("tab=import");
  }
  if (item.id === "calendar") {
    return (
      (pathname === "/attendance/calendar" ||
      pathname.startsWith("/attendance/apply-calendar")) &&
      !search.includes("tab=import")
    );
  }
  if (item.id === "holidays") {
    return pathname.startsWith("/attendance/holiday-calendar") || pathname === "/holiday-calendar";
  }
  if (item.id === "mark") {
    return pathname.startsWith("/attendance/mark");
  }
  if (item.id === "records") {
    return pathname.startsWith("/attendance/records");
  }
  const path = item.path.split("?")[0];
  return pathname === path || pathname.startsWith(`${path}/`);
}
